import { useCallback, useContext, useEffect } from "react"
import { useFormDispatch, useFormSelector } from "../../../redux/formStoreHooks"
import { ConceptValueDataType } from '@fusion-advantage/backcompat'
import { NeurologicWeaknessConcept, NeurologicWeaknessItem, WeaknessSite } from "@fusion-advantage/formlib"
import L6FormContext from "../l6/L6FormContext"
import { setNeurologicCurrentModel, setNeurologicWeaknessReview, setNeurologicSelectedHumanoidSite, setNeurologicShowDialog } from "../../../redux/neurologicWeaknessReviewSlice"
import L3NeurologicWeaknessList from "../l3/L3NeurologicWeaknessList"
import _ from 'lodash'
import L3NeurologicWeaknessHumanoid from "../l3/L3NeurologicWeaknessHumanoid"
import { HumanDiagram } from "../l1/L1HumanDiagram"
import { useConceptValidator } from "./useConceptValidator"

interface L4NeurologicWeaknessListProps {
	xpath?: string
}

export enum NeurologicWeaknessDiagram {
	Front = 'FRONT',
	Back = 'BACK'
}

export interface DiagramWeakness {
	cx: number,
	cy: number,
	rotateDeg?: number,
	site: WeaknessSite,
	diagram: HumanDiagram,
	height?: number,
	width?: number,
	label?: string,
}

export const WEAKNESSES: DiagramWeakness[] = [
	{ cx: -15, cy: 90, site: WeaknessSite.HemiLeftSide, diagram: HumanDiagram.HumanoidBack, width: 15, height: 300, label: WeaknessSite.HemiLeftSide },
	{ cx: 220, cy: 90, site: WeaknessSite.HemiRightSide, diagram: HumanDiagram.HumanoidBack, width: 15, height: 300, label: WeaknessSite.HemiRightSide },
	{ cx: 75, cy: 40, site: WeaknessSite.RightFacial, diagram: HumanDiagram.Humanoid },
	//{ cx: 33, cy: 200, site: WeaknessSite.RightLowerForearm, diagram: HumanDiagram.Humanoid },
	{ cx: 83, cy: 340, site: WeaknessSite.RightLowerLeg, diagram: HumanDiagram.Humanoid },
	{ cx: 40, cy: 170, site: WeaknessSite.RightUpperArm, diagram: HumanDiagram.Humanoid },
	//{ cx: 78, cy: 260, site: WeaknessSite.RightUpperLegThigh, diagram: HumanDiagram.Humanoid },
	{ cx: 104, cy: 290, site: WeaknessSite.LowerLimbsBothLegs, diagram: HumanDiagram.HumanoidBack, width: 80, height: 150, label: "Lower limbs" },
	{ cx: 102, cy: 465, site: WeaknessSite.FourLimbs, diagram: HumanDiagram.HumanoidBack, width: 200, height: 15, label: WeaknessSite.FourLimbs },
	{ cx: 134, cy: 40, site: WeaknessSite.LeftFacial, diagram: HumanDiagram.Humanoid },
	//{ cx: 173, cy: 200, site: WeaknessSite.LeftLowerForearm, diagram: HumanDiagram.Humanoid },
	{ cx: 123, cy: 340, site: WeaknessSite.LeftLowerLeg, diagram: HumanDiagram.Humanoid },
	{ cx: 165, cy: 170, site: WeaknessSite.LeftUpperArm, diagram: HumanDiagram.Humanoid },
	//{ cx: 128, cy: 260, site: WeaknessSite.LeftUpperLegThigh, diagram: HumanDiagram.Humanoid },
	{ cx: 103, cy: 80, site: WeaknessSite.UpperLimbsBothArms, diagram: HumanDiagram.HumanoidBack, width: 80, height: 75, label: "Upper limbs" },
]

export const L4NeurologicWeaknessHumanoid = () => {
	const dispatch = useFormDispatch()
	const models = useFormSelector(state => state.neurologicWeaknessReview.models)
	const selectedSite = useFormSelector(state => state.neurologicWeaknessReview.selectedHumanoidSite)
	
	const onSelectSite = useCallback((site: string) => {
		dispatch(setNeurologicSelectedHumanoidSite(site))
		dispatch(setNeurologicCurrentModel(models.find(x => x.site === site) || null))
		dispatch(setNeurologicShowDialog(true))
	}, [models])

	return (
		<L3NeurologicWeaknessHumanoid 
			models={models}
			diagramWeaknessModels={WEAKNESSES}
			selectedSite={selectedSite}
			onSelectSite={onSelectSite}
		/>
	)
}

export const L4NeurologicWeaknessReview = ({xpath}: L4NeurologicWeaknessListProps) => {
	const { errorNode } = useConceptValidator({
		tagName: 'L4NeurologicWeaknessReview',
		cid: NeurologicWeaknessConcept.cid,
		requireConcept: true,
		allowDataTypes: [ConceptValueDataType.Object],
	})
	const dispatch = useFormDispatch()
	const models: NeurologicWeaknessItem[] = useFormSelector(state => state.neurologicWeaknessReview.models)
	const selectedSite = useFormSelector(state => state.neurologicWeaknessReview.selectedHumanoidSite)
	const showDialog = useFormSelector(state => state.neurologicWeaknessReview.showDialog)
	const currentModel = useFormSelector(state => state.neurologicWeaknessReview.currentModel)
	
	const shouldPrint = useCallback(() => {
		return models.length > 0
	}, [models])
	
	const { setXpathValuedDeterminer } = useContext(L6FormContext)
	
	useEffect(() => {
		if(xpath) {
			setXpathValuedDeterminer(xpath, shouldPrint)
			return () => {
				setXpathValuedDeterminer(xpath, false)
			}
		}
	}, [xpath, shouldPrint, setXpathValuedDeterminer])

	const handleHideDialog = useCallback(() => {
		dispatch(setNeurologicShowDialog(false))
		dispatch(setNeurologicSelectedHumanoidSite(null))
	}, [])

	const handleChange = useCallback((changedModels: NeurologicWeaknessItem[]) => {
		dispatch(setNeurologicWeaknessReview(changedModels))

		dispatch(setNeurologicShowDialog(false))
		dispatch(setNeurologicSelectedHumanoidSite(null))
	}, [dispatch])

	if(errorNode) {
		return errorNode
	}
	return (
		<L3NeurologicWeaknessList
			models={models}
			currentModel={currentModel}
			onChange={handleChange}
			selectedSite={selectedSite}
			showDialog={showDialog}
			onHideDialog={handleHideDialog}
		/>
	)
}